import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useOnboardingStore } from '../../stores/useOnboardingStore';
import {
  CHECKLIST_ITEMS,
  CHECKLIST_HIGHLIGHT_MESSAGES,
} from '../../models/onboarding.constants';
import { isChecklistItemDone } from '../../models/checklistProgress';
import styles from './SetupChecklist.module.css';

const SetupChecklist = () => {
  const isChecklistOpen = useOnboardingStore((s) => s.isChecklistOpen);
  const toggleChecklist = useOnboardingStore((s) => s.toggleChecklist);
  const checklist = useOnboardingStore((s) => s.checklist);
  const hasBillingRuc = useOnboardingStore((s) => s.hasBillingRuc);
  const tours = useOnboardingStore((s) => s.tours);
  const firstSale = useOnboardingStore((s) => s.firstSale);
  const completionPct = useOnboardingStore((s) => s.completionPct);
  const checklistStatus = useOnboardingStore((s) => s.checklistStatus);
  const highlightedChecklistItem = useOnboardingStore((s) => s.highlightedChecklistItem);
  const showChecklistIntro = useOnboardingStore((s) => s.showChecklistIntro);
  const dismissChecklistIntro = useOnboardingStore((s) => s.dismissChecklistIntro);
  const startChecklistTour = useOnboardingStore((s) => s.startChecklistTour);
  const skipOnboarding = useOnboardingStore((s) => s.skipOnboarding);
  const navigate = useNavigate();
  const highlightedRef = useRef<HTMLLIElement>(null);
  const [confirmingSkip, setConfirmingSkip] = useState(false);

  // Scroll to highlighted item
  useEffect(() => {
    if (highlightedChecklistItem && highlightedRef.current) {
      highlightedRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightedChecklistItem]);

  if (!isChecklistOpen) return null;

  const isItemCompleted = (key: string): boolean =>
    isChecklistItemDone(key, { checklist, checklistStatus, tours, firstSale, hasBillingRuc });

  const completedCount = CHECKLIST_ITEMS.filter((item) => isItemCompleted(item.key)).length;

  const handleItemClick = (item: typeof CHECKLIST_ITEMS[number]) => {
    if (isItemCompleted(item.key)) return;
    dismissChecklistIntro();
    navigate(item.route);
    startChecklistTour(item.key);
  };

  const highlightMessage = highlightedChecklistItem
    ? CHECKLIST_HIGHLIGHT_MESSAGES[highlightedChecklistItem]
    : null;

  return (
    <>
      {/* Backdrop overlay when intro spotlight is shown */}
      {showChecklistIntro && createPortal(
        <div className={styles.introOverlay} onClick={dismissChecklistIntro} />,
        document.body,
      )}

      <div className={`${styles.panel} ${showChecklistIntro ? styles.panelSpotlight : ''}`}>
        <div className={styles.header}>
          <h3 className={styles.headerTitle}>Configura tu negocio</h3>
          <button className={styles.closeBtn} onClick={toggleChecklist}>
            &times;
          </button>
        </div>

        <div className={styles.progressSection}>
          <p className={styles.progressLabel}>
            Progreso: {completionPct}% ({completedCount}/{CHECKLIST_ITEMS.length} tareas)
          </p>
          <div className={styles.progressBarBg}>
            <div
              className={styles.progressBarFill}
              style={{ width: `${completionPct}%` }}
            />
          </div>
        </div>

        {/* Intro message when first shown */}
        {showChecklistIntro && highlightMessage && (
          <div className={styles.introMessage}>
            <p className={styles.introText}>{highlightMessage}</p>
          </div>
        )}

        <div className={styles.content}>
          <ul className={styles.itemsList}>
            {CHECKLIST_ITEMS.map((item) => {
              const completed = isItemCompleted(item.key);
              const isHighlighted = highlightedChecklistItem === item.key;
              // Disable firstSale until employees + services + products are done
              const isLocked = item.key === 'firstSale' && (
                !isItemCompleted('employees') || !isItemCompleted('services') || !isItemCompleted('products')
              );

              return (
                <li
                  key={item.key}
                  ref={isHighlighted ? highlightedRef : undefined}
                  className={`${styles.item} ${completed ? styles.itemCompleted : ''} ${isHighlighted ? styles.itemHighlighted : ''} ${isLocked ? styles.itemLocked : ''}`}
                  onClick={() => !isLocked && handleItemClick(item)}
                >
                  <div
                    className={`${styles.itemCheck} ${completed ? styles.itemCheckCompleted : ''}`}
                  >
                    {completed && '\u2713'}
                  </div>
                  <div className={styles.itemText}>
                    <p className={styles.itemLabel}>{item.label}</p>
                    <p className={styles.itemDesc}>
                      {isLocked ? 'Completa las tareas anteriores primero' : item.description}
                    </p>
                  </div>
                  {!completed && !isLocked && (
                    <span className={styles.itemArrow}>&rsaquo;</span>
                  )}
                  {isLocked && (
                    <span className={styles.itemLockIcon}>🔒</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        <div className={styles.skipFooter}>
          {confirmingSkip ? (
            <div className={styles.skipConfirm}>
              <p className={styles.skipConfirmText}>
                Esta guía no volverá a mostrarse. ¿Deseas omitirla?
              </p>
              <div className={styles.skipConfirmActions}>
                <button
                  className={styles.skipConfirmBtn}
                  onClick={() => skipOnboarding()}
                >
                  Sí, omitir
                </button>
                <button
                  className={styles.skipCancelBtn}
                  onClick={() => setConfirmingSkip(false)}
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <button
              className={styles.skipLink}
              onClick={() => setConfirmingSkip(true)}
            >
              Omitir esta configuración
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default SetupChecklist;
